import { Link } from "react-router-dom";
import { Wrench, ArrowRight } from "lucide-react";
import { getRelatedToolsForInsight } from "@/data/toolInsightLinks";
import { analytics } from "@/hooks/useAnalytics";

// Tool metadata for display - matches ToolPage.tsx config
const toolMeta: Record<string, { title: string; description: string; category: string }> = {
  "email-checker": { title: "Email Deliverability Tester", description: "Test your email deliverability with SPF, DKIM, DMARC analysis", category: "Deliverability" },
  "auth-checker": { title: "SPF/DKIM/DMARC Checker", description: "Verify email authentication DNS records instantly", category: "Authentication" },
  "blacklist-checker": { title: "IP & Domain Blacklist Scanner", description: "Check if you're on major email blocklists", category: "Authentication" },
  "header-analyzer": { title: "Email Header Decoder", description: "Debug deliverability with AI header analysis", category: "Deliverability" },
  "tab-predictor": { title: "Gmail Tab Predictor", description: "Predict Primary vs Promotions placement", category: "Deliverability" },
  "content-analyzer": { title: "Accessibility & Mobile Checker", description: "AI-powered accessibility analysis", category: "Content" },
  "email-preview": { title: "Email Client Preview", description: "See rendering across email clients", category: "Content" },
  "subject-analyzer": { title: "Spam Trigger Analyzer", description: "AI detection of spam triggers", category: "Content" },
  "bulk-subject-analyzer": { title: "Bulk Subject Line Analyzer", description: "Compare multiple subject lines", category: "Content" },
  "seo-meta-analyzer": { title: "SEO Meta Tag Analyzer", description: "Optimize meta tags for search", category: "SEO" },
  "robots-checker": { title: "Robots Meta Checker", description: "Analyze search indexing directives", category: "SEO" },
  "phishing-detector": { title: "Phishing Email Detector", description: "Analyze emails for phishing indicators", category: "Security" },
  "domain-age-checker": { title: "Domain Age Checker", description: "Look up domain registration date", category: "Authentication" },
  "link-validator": { title: "Link Validator", description: "Check URLs for safety and redirects", category: "Security" },
  "dmarc-analyzer": { title: "DMARC Report Analyzer", description: "Parse DMARC aggregate reports", category: "Authentication" },
  "m365-tenant-analyzer": { title: "Google/Microsoft 365 Analyzer", description: "Find tenant and domain info", category: "Authentication" },
  "dns-propagation-checker": { title: "DNS Propagation Checker", description: "Check DNS record propagation globally", category: "Authentication" },
  "pm-cost-calculator": { title: "PM Tool Cost Calculator", description: "Compare project management tool costs", category: "Productivity" },
  "meeting-cost-calculator": { title: "Meeting Cost Calculator", description: "Calculate the true cost of meetings", category: "Productivity" },
  "collaboration-comparison": { title: "Team Collaboration Comparison", description: "Compare Slack, Teams, Notion & more", category: "Productivity" },
  "global-latency-tester": { title: "Global Latency Tester", description: "Test response times from 8 regions", category: "Developer" },
  "webhook-debugger": { title: "Webhook Debugger", description: "Catch and inspect webhooks in real-time", category: "Developer" },
  "api-response-simulator": { title: "API Response Simulator", description: "Create mock API endpoints for testing", category: "Developer" },
};

interface RelatedToolsProps {
  insightSlug: string;
  variant?: "sidebar" | "inline" | "card";
  limit?: number;
}

export const RelatedTools = ({ insightSlug, variant = "card", limit = 3 }: RelatedToolsProps) => {
  const relatedToolIds = getRelatedToolsForInsight(insightSlug);
  const tools = relatedToolIds.slice(0, limit).map(id => ({
    id,
    ...toolMeta[id],
  })).filter(t => t.title);

  if (tools.length === 0) return null;

  const handleToolClick = (toolId: string, toolTitle: string) => {
    analytics.ctaClicked(`related_tool_${toolId}`, `/tools/${toolId}`);
  };

  if (variant === "sidebar") {
    return (
      <aside className="bg-muted/30 rounded-xl border border-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <Wrench className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm text-foreground">Try These Tools</h3>
        </div>
        <div className="space-y-3">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={`/tools/${tool.id}`}
              onClick={() => handleToolClick(tool.id, tool.title)}
              className="block p-3 rounded-lg bg-background hover:bg-primary/5 border border-border hover:border-primary/30 transition-all group"
            >
              <span className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                {tool.title}
              </span>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {tool.description}
              </p>
            </Link>
          ))}
        </div>
        <Link
          to="/tools"
          className="flex items-center justify-center gap-1 mt-4 text-xs text-primary hover:text-primary/80 transition-colors"
        >
          View all tools
          <ArrowRight className="w-3 h-3" />
        </Link>
      </aside>
    );
  }

  if (variant === "inline") {
    return (
      <div className="my-8 p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">Try It Now</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Put this knowledge into practice with our free tools:
        </p>
        <div className="flex flex-wrap gap-2">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={`/tools/${tool.id}`}
              onClick={() => handleToolClick(tool.id, tool.title)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background border border-border hover:border-primary hover:bg-primary/5 transition-all text-sm font-medium text-foreground hover:text-primary"
            >
              {tool.title}
              <ArrowRight className="w-3 h-3" />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Default: card variant
  return (
    <section className="my-10 sm:my-12">
      <div className="flex items-center gap-2 mb-6">
        <Wrench className="w-5 h-5 text-primary" />
        <h2 className="font-display text-xl font-semibold text-foreground">
          Try These Tools
        </h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tools.map((tool) => (
          <Link
            key={tool.id}
            to={`/tools/${tool.id}`}
            onClick={() => handleToolClick(tool.id, tool.title)}
            className="group p-5 rounded-xl bg-card border border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Wrench className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {tool.category}
              </span>
            </div>
            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
              {tool.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {tool.description}
            </p>
            <div className="flex items-center gap-1 mt-3 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
              Try it free
              <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
